import React, { useContext } from 'react'
import { AlertCircle, CheckCircle } from 'react-feather'
import styled, { ThemeContext } from 'styled-components/macro'
import { Web3Context } from '../../../hooks/web3'
import { AutoColumn } from '../../catalog/layout/AutoColumn'
import { useTransaction } from '../../transactions/TransactionContext'
import { POLYGON_CONFIG } from '../../../chain/polygon-mainnet'
import { Transaction } from '../../../logic/transactions/transactions'
import { fromWei } from '../../../utils/static-web3'
import { SmallCurrencyLogo } from '../../CurrencyLogo'
import { coinImage } from '../../../assets/coins/coins'

const RowNoFlex = styled.div`
  display: flex;
  flex-wrap: nowrap;
`

export default function TransactionPopup({ hash }: { hash: string }) {
  const { appChainID } = useContext(Web3Context)

  const tx = useTransaction(hash)

  if (!tx) return null

  const success = tx.status === 'success'

  /* Default content, but should be always overwritten */
  let content = success ? (
    <div>Transaction succeed</div>
  ) : (
    <div>Transaction failed</div>
  )

  if (tx.type === 'swap') {
    content = success ? SwapSummary(tx) : FailedSwap(tx)
  }

  if (tx.type === 'deposit-multi') {
    content = success ? DepositMultiSummary(tx) : DepositFailed(tx)
  }
  if (tx.type === 'deposit-one') {
    content = success ? DepositOneSummary(tx) : DepositFailed(tx)
  }
  if (tx.type === 'withdraw-multi' || tx.type === 'withdraw-one') {
    content = success ? WithdrawSummary(tx) : WithdrawalFailed(tx)
  }

  return (
    <RowNoFlex>
      <div style={{ paddingRight: 16 }}>
        {success ? (
          <CheckCircle color={'green'} size={24} />
        ) : (
          <AlertCircle color={'red'} size={24} />
        )}
      </div>
      <AutoColumn gap="8px">
        {content}
        <div>
          {appChainID && hash && (
            <a
              className="mt5 link fs12"
              href={POLYGON_CONFIG.getExplorerUrl(hash!)}
              target="_blank"
            >
              <div>
                <p>View on Explorer</p>
              </div>
            </a>
          )}
        </div>
      </AutoColumn>
    </RowNoFlex>
  )
}

function SwapSummary(tx: Transaction) {
  const { tokenIn, tokenOut, inAmountWei, realOutAmountWei } = tx.data
  const amountIn = fromWei(inAmountWei, tokenIn.decimals).toFixed(6)
  const amountOut = fromWei(realOutAmountWei, tokenOut.decimals).toFixed(6)
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'middle',
      }}
    >
      <span>
        Successfully swaped {amountIn}{' '}
        {
          <span style={{ marginLeft: '5px', verticalAlign: 'middle' }}>
            <SmallCurrencyLogo src={coinImage(tokenIn.symbol)!} key={'in'} />
          </span>
        }{' '}
        for {amountOut}{' '}
        {<SmallCurrencyLogo src={coinImage(tokenOut.symbol)!} key={'out'} />}
      </span>
    </div>
  )
}

function FailedSwap(tx: Transaction) {
  const { tokenIn, tokenOut, inAmountWei, realOutAmountWei } = tx.data

  return (
    <div>
      Failed to swap {tokenIn.symbol} to {tokenOut.symbol}
    </div>
  )
}

function DepositMultiSummary(tx: Transaction) {
  const { amounts } = tx.deposit!
  const tokens = Array.from(amounts!.keys())
    .map(t => t.symbol)
    .join(', ')

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'middle',
      }}
    >
      <span>
        Successfully deposited{' '}
        {
          <span style={{ marginLeft: '5px', verticalAlign: 'middle' }}>
            {Array.from(amounts!.keys()).map(t => (
              <SmallCurrencyLogo src={coinImage(t.symbol)!} key={t.symbol} />
            ))}
          </span>
        }
      </span>
    </div>
  )
}
function DepositOneSummary(tx: Transaction) {
  let { token, amount } = tx.deposit!

  // old transactions, should be fixed
  if (!token) {
    token = tx.data.token
  }
  const tokenAmount = fromWei(amount!, token!.decimals).toFixed(6)
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <span>
        Successfully deposited {tokenAmount}
        {
          <span style={{ marginLeft: '5px', verticalAlign: 'middle' }}>
            <SmallCurrencyLogo src={coinImage(token!.symbol)!} />
          </span>
        }
      </span>
    </div>
  )
}

function DepositFailed(tx: Transaction) {
  return <div>Failed to deposit</div>
}

function WithdrawSummary(tx: Transaction) {
  const { amounts } = tx.withdrawal!
  const tokens = Array.from(amounts!.keys())
    .map(t => t.symbol)
    .join(', ')

  return (
    <div>
      Successfully withdrew{' '}
      {
        <span style={{ marginLeft: '5px', verticalAlign: 'middle' }}>
          {Array.from(amounts!.keys()).map(t => (
            <SmallCurrencyLogo src={coinImage(t.symbol)!} key={t.symbol} />
          ))}
        </span>
      }
    </div>
  )
}

function WithdrawalFailed(tx: Transaction) {
  const { amounts } = tx.withdrawal!
  const tokens = Array.from(amounts!.keys())
    .map(t => t.symbol)
    .join(', ')

  return (
    <div>
      Failed to withdraw{' '}
      {
        <span style={{ marginLeft: '5px', verticalAlign: 'middle' }}>
          {Array.from(amounts!.keys()).map(t => (
            <SmallCurrencyLogo src={coinImage(t.symbol)!} key={t.symbol} />
          ))}
        </span>
      }
    </div>
  )
}
